import { ContentServerProxyImpl } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/contentServerProxy';
import { getSearchAppName, calculatePercentChange } from 'src/utils/app';
import axios from 'axios';
import {
  CategoryType,
  ContentMetricType,
  ContentScores,
  GetContentResponse,
  GetScoreForContentMetricByCategoryTypeRequest,
  GetScoreForContentMetricByWeekResponse,
  GetScoreForContentMetricRequest,
  GetVoiceAndToneCurrentAndTargetVectorsResponse,
  GetWeightedKeywordsAndPhrasesForProductResponse,
  KeywordPerformanceScores,
  TimePeriodRange,
  VoiceAndToneMatchType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import {
  useBaseContentRequestBuilder,
  useContentMetricsForProductRequestBuilder,
  useWeightedKeywordsAndPhrasesForProductRequestBuilder
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useBaseContentRequestBuilder';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';

import { useRetailerId, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { ComparisonPeriod, useAppSelector, useComparisonPeriod, useWeekRange, WeekRange } from 'src/utils/Hooks';
import {
  ChartDataAdapter,
  Product
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { KeyMetricsDataAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import {
  ContentScoresByWeekAdapter,
  TableGridAdapter,
  VoiceAndToneMatchAnalysisAdapter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/TableGridAdapter';
import { TimeSeriesData } from './classes/TimeSeriesData';
import {
  CategoryScoreAdapter,
  VoiceAndToneProductCountAdapter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/CategoryScoreAdapter';
import { TimeSeriesAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/classes/TimeSeriesAdapter';
import { ProductContentUtilizationAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductContentUtilizationAdapter';
import { ProductContentAccuracyAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductContentAccuracyAdapter';
import {
  ContentSplineChartData,
  KeywordFilter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import { getFirstWeekIdOfYear, getPreviousWeekId, getPreviousYearWeekId, yearPartOfWeekId } from 'src/utils/dateUtils';
import { KeywordsPerformanceSummaryAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeywordsPerformanceSummaryAdapter';
import { ProductLevelKeywordsDetailsAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductLevelKeywordsDetailsAdapter';
import { ProductLevelVoiceAndToneAnalysisAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductLevelVoiceAndToneAnalysisAdapter';
import { ProductLevelContentAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductLevelContentAdapter';
import { apiCompareProductKeywords } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import {
  ProductKeywordsComparisonRequest,
  ComparisonAnalysis
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';

/**
 * Higher-order function that wraps async operations with error handling
 * @param operation - The async operation to execute
 * @param defaultValue - The fallback value to return if the operation fails
 * @param errorMessage - The message to log if an error occurs
 * @returns Promise resolving to either the operation result or the default value
 */
const withErrorHandling = async <T>(operation: () => Promise<T>, defaultValue: T, errorMessage: string): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    console.error(errorMessage, error);
    return defaultValue;
  }
};

/**
 * Custom hook that provides content-related data fetching services
 */
export const useContentService = () => {
  const requestBuilder = useBaseContentRequestBuilder();
  const productRequestBuilder = useContentMetricsForProductRequestBuilder();
  const weightedKeywordsAndPhrasesRequestBuilder = useWeightedKeywordsAndPhrasesForProductRequestBuilder();

  const client = new ContentServerProxyImpl();
  const retailerId = useRetailerId();
  const { mainTimePeriod: defaultMainTimePeriod, comparisonTimePeriod: defaultComparisonTimePeriod } = useTimePeriods();
  const comparisonPeriod = useComparisonPeriod();

  const weekRange = useWeekRange();

  const categoryNames = useAppSelector((state) =>
    state.categories.reduce((acc, category) => {
      acc[category.id] = category.displayName;
      return acc;
    }, {} as Record<number, string>)
  );
  const subcategoryNames = useAppSelector((state) =>
    state.subCategories.reduce((acc, subcategory) => {
      acc[subcategory.id] = subcategory.displayName;
      return acc;
    }, {} as Record<number, string>)
  );
  const brandNames = useAppSelector((state) =>
    state.brandsFollowing.reduce((acc, brand) => {
      acc[brand.brandId] = brand.displayName;
      return acc;
    }, {} as Record<number, string>)
  );

  /**
   * Internal helper function that fetches content key metrics for a single time period
   * @param timePeriod - The time period range containing start and end weeks to fetch data for
   * @returns Promise resolving to ContentScores containing scores
   */
  const getKeyMetricsInner = async (metricType: ContentMetricType, timePeriod: TimePeriodRange) => {
    return withErrorHandling(
      async () => {
        switch (metricType) {
          case ContentMetricType.ContentScore:
            return (
              await client.getContentScoreSummary(
                requestBuilder.clone().setStartWeekId(timePeriod.startWeek).setEndWeekId(timePeriod.endWeek).build()
              )
            ).scores;
          case ContentMetricType.ContentUtilizationScore:
            return (
              await client.getContentUtilizationSummary(
                requestBuilder.clone().setStartWeekId(timePeriod.startWeek).setEndWeekId(timePeriod.endWeek).build()
              )
            ).contentUtilizationScores;
          case ContentMetricType.ContentAccuracyScore:
            return (
              await client.getContentAccuracySummary(
                requestBuilder.clone().setStartWeekId(timePeriod.startWeek).setEndWeekId(timePeriod.endWeek).build()
              )
            ).contentAccuracyScores;
          default:
            throw new Error(`Unsupported metric type: ${metricType}`);
        }
      },
      {} as ContentScores,
      `Error fetching content utilization key metrics for time period ${timePeriod}`
    );
  };

  /**
   * Fetches and compares content utilization metrics between two time periods.
   * @param mainTimePeriod - The primary time period to analyze, defaults to current selected period
   * @param comparisonTimePeriod - The secondary time period for comparison, defaults to current comparison period
   * @returns Promise resolving to a KeyMetricsDataAdapter containing the metrics data for both periods
   */
  const getContentScoresKeyMetrics = async (
    metricType: ContentMetricType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainMetrics, comparisonMetrics] = await Promise.all([
      getKeyMetricsInner(metricType, mainTimePeriod),
      getKeyMetricsInner(metricType, comparisonTimePeriod)
    ]);

    return new KeyMetricsDataAdapter(mainMetrics, comparisonMetrics, metricType);
  };

  /**
   * Fetches scores by week for a given content metric request
   * @param contentMetricType - The type of content metric to analyze
   * @param timePeriod - The time period range containing start and end weeks to fetch data for
   * @returns Promise resolving to an array of [timestamp, score] pairs
   */
  const getScoresByWeek = async (
    request: GetScoreForContentMetricRequest
  ): Promise<GetScoreForContentMetricByWeekResponse> => {
    return withErrorHandling(
      async () => {
        const response = await client.getScoreForContentMetricByWeek(request);
        return response;
      },

      { records: [] },
      'Error fetching scores by week'
    );
  };

  /**
   * Retrieves content scores over time for both main and comparison time periods
   */
  const getContentScoresByWeek = async (
    contentMetricType: ContentMetricType,
    mainTimePeriod: TimePeriodRange,
    comparisonTimePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ): Promise<ContentSplineChartData> => {
    return withErrorHandling(
      async () => {
        const request = requestBuilder
          .clone()
          .setContentMetricType(contentMetricType)
          .setVoiceAndToneMatchTypes(voiceAndToneMatchTypes);

        const isLastWeekWithPriorYear =
          weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

        const mainPeriodStart = isLastWeekWithPriorYear
          ? getFirstWeekIdOfYear(yearPartOfWeekId(defaultMainTimePeriod.endWeek))
          : mainTimePeriod.startWeek;
        const mainTimePeriodReqBuilder = request
          .clone()
          .setStartWeekId(mainPeriodStart)
          .setEndWeekId(mainTimePeriod.endWeek);

        const comparisonPeriodStart = isLastWeekWithPriorYear
          ? getPreviousYearWeekId(mainPeriodStart)
          : comparisonTimePeriod.startWeek;

        const comparisonPeriodEnd =
          isLastWeekWithPriorYear || (weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR)
            ? getPreviousWeekId(mainPeriodStart)
            : comparisonTimePeriod.endWeek;

        const comparisonTimePeriodReqBuilder = request
          .clone()
          .setStartWeekId(comparisonPeriodStart)
          .setEndWeekId(comparisonPeriodEnd);

        const primaryScores = await getScoresByWeek(
          mainTimePeriodReqBuilder.build() as GetScoreForContentMetricRequest
        );
        const secondaryScores = await getScoresByWeek(
          comparisonTimePeriodReqBuilder.build() as GetScoreForContentMetricRequest
        );

        // If it's last week with prior year comparison, we only want to use
        // the last week's data for calculating averages
        const primarySeries = new TimeSeriesData([]).createWeekRecords(primaryScores.records, (record) => record.score);

        const comparisonSeries = new TimeSeriesData([]).createWeekRecords(
          secondaryScores.records,
          (record) => record.score
        );

        const { startWeek, endWeek } = mainTimePeriod;
        const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = comparisonTimePeriod;

        const primaryAverage = primarySeries.compute('average', {
          range: [startWeek, endWeek]
        });

        const comparisonAverage = comparisonSeries.compute('average', {
          range: [comparisonStartWeek, comparisonEndWeek]
        });

        const percentChange = calculatePercentChange(primaryAverage, comparisonAverage);

        const timeSeriesAdapter = new TimeSeriesAdapter(primarySeries, comparisonSeries, comparisonPeriod);
        const chartData = timeSeriesAdapter.transform();

        return {
          chartData,
          legendData: {
            primaryLegendMetric: primaryAverage,
            secondaryLegendMetric: comparisonAverage,
            percentChange
          }
        };
      },

      {
        chartData: { primaryMetrics: [], secondaryMetrics: [] },
        legendData: { primaryLegendMetric: 0, secondaryLegendMetric: 0, percentChange: 0 }
      },
      'Error fetching content scores by week'
    );
  };

 const getProducts = async (stacklineSkus: string[]): Promise<Product[]> => {
    return withErrorHandling(
      () =>
        axios
          .post<Product[]>('/api/beacon/GetEntityMetadataInBulk', {
            entityType: 'product',
            entityIds: stacklineSkus,
            retailerId
          })
          .then((response) => response.data),
      [],
      `Error fetching products for Stackline SKUs ${stacklineSkus.join(', ')}`
    );
  };

  /**
   * Creates a mapping of Stackline SKUs to their corresponding Product objects
   * @param productSkus - Array of Stackline SKU strings to fetch product data for
   * @returns Promise resolving to a Record mapping SKUs to Product objects
   */
  const getProductsMap = async (productSkus: string[]): Promise<Record<string, Product>> => {
    return withErrorHandling(
      async () => {
        const products = await getProducts(productSkus);
        return products.reduce((acc, product) => {
          acc[product.stacklineSku] = product;
          return acc;
        }, {} as Record<string, Product>);
      },
      {},
      'Error fetching products map'
    );
  };

  /**
   * Internal helper function to fetch content scores for all products for a specific time period
   * @param contentMetricType - The type of content metric to analyze (e.g. ContentUtilizationScore)
   * @param timePeriod - Time range to fetch scores for, containing start and end week IDs
   * @returns Promise resolving to an array of content metric score records by product
   */
  const getContentScoresByProductInner = async (
    contentMetricType: ContentMetricType,
    voiceAndToneMatchTypes: VoiceAndToneMatchType[],
    timePeriod: TimePeriodRange
  ) => {
    return withErrorHandling(
      async () => {
        const req = requestBuilder
          .clone()
          .setContentMetricType(contentMetricType)
          .setVoiceAndToneMatchTypes(voiceAndToneMatchTypes)
          .setMainTimePeriod(timePeriod)
          .build() as GetScoreForContentMetricRequest;
        const response = await client.getScoreForContentMetricByProduct(req);
        return response.records;
      },
      [],
      `Error fetching ${contentMetricType} scores by product for time period ${timePeriod}`
    );
  };

  /**
   * Retrieves content scores for all products across two time periods
   * @param contentMetricType - The type of content metric to analyze (e.g. ContentUtilizationScore)
   * @param mainTimePeriod - Primary time range to fetch scores for, defaults to the default main time period
   * @param comparisonTimePeriod - Secondary time range to compare against, defaults to the default comparison time period
   * @returns Promise resolving to a TableGridAdapter that can transform the data to be rendered in a table grid
   */
  const getContentScoresByProduct = async (
    contentMetricType: ContentMetricType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ): Promise<ChartDataAdapter<unknown>> => {
    const [mainProductRecords, comparisonProductRecords] = await Promise.all([
      getContentScoresByProductInner(contentMetricType, voiceAndToneMatchTypes, mainTimePeriod),
      getContentScoresByProductInner(contentMetricType, voiceAndToneMatchTypes, comparisonTimePeriod)
    ]);
    const products = await getProductsMap(mainProductRecords.map((record) => record.stacklineSku));

    if (contentMetricType === ContentMetricType.VoiceAndToneMatchAnalysis) {
      return new VoiceAndToneMatchAnalysisAdapter(mainProductRecords, comparisonProductRecords, products);
    }

    return new TableGridAdapter(mainProductRecords, comparisonProductRecords, products);
  };

  const getContentScoreByCategoryInner = async (
    contentMetricType: ContentMetricType,
    categoryType: CategoryType,
    timePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    return withErrorHandling(
      async () => {
        const req = requestBuilder
          .clone()
          .setContentMetricType(contentMetricType)
          .setCategoryType(categoryType)
          .setVoiceAndToneMatchTypes(voiceAndToneMatchTypes)
          .setMainTimePeriod(timePeriod)
          .build();

        const response = await client.getScoreForContentMetricByCategoryType(
          req as GetScoreForContentMetricByCategoryTypeRequest
        );
        return response.records;
      },
      [],
      `Error fetching ${contentMetricType} scores by ${categoryType} for time period ${timePeriod}`
    );
  };

  const getBeaconBasicContentScore = async (groupByField: string, timePeriod: TimePeriodRange) => {
    const builder = new AdvancedSearchRequestBuilder(
      'beacon-content-score',
      'beacon-new-content-metrics'
    ).setAggregation({
      groupByFieldName: groupByField as any,
      aggregationFields: [
        {
          aggregateByFieldDisplayName: 'Weighted Content Score',
          aggregateByFieldName: 'weightedContentScoreByUnits',
          function: 'sum'
        },
        {
          aggregateByFieldDisplayName: 'Content Utilization',
          aggregateByFieldName: 'contentScore' as any,
          function: 'computed',
          aggregateByFormula:
            '((unitsSoldForContentScore_sum > 0) ? ((weightedContentScoreByUnits_sum / unitsSoldForContentScore_sum)) : (0))'
        },
        {
          aggregateByFieldDisplayName: 'Units Sold for Content Score',
          aggregateByFieldName: 'unitsSoldForContentScore' as any,
          function: 'sum'
        }
      ]
    });

    return withErrorHandling(
      async () => {
        const response = await axios.post(`/api/${getSearchAppName()}/AdvancedSearch?helloworld`, [builder.build()]);
        return response.data;
      },
      [],
      `Error fetching for time period ${timePeriod}`
    );
  };

  /**
   * Retrieves content scores grouped by category type
   * @param contentMetricType - The type of content metric to analyze
   * @param categoryType - The category classification to group by
   * @returns Promise containing an array of category scores
   */
  const getBeaconContentScoreByCategory = async (
    groupBy: string,
    categoryType: CategoryType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainPeriodResponse, comparisonPeriodResponse] = await Promise.all([
      getBeaconBasicContentScore(groupBy, mainTimePeriod),
      getBeaconBasicContentScore(groupBy, comparisonTimePeriod)
    ]);

    return new CategoryScoreAdapter(categoryType, [mainPeriodResponse, comparisonPeriodResponse], {
      category: categoryNames,
      subcategory: subcategoryNames,
      brand: brandNames
    });
  };

  /**
   * Retrieves content scores grouped by category type
   * @param contentMetricType - The type of content metric to analyze
   * @param categoryType - The category classification to group by
   * @returns Promise containing an array of category scores
   */
  const getContentScoreByCategory = async (
    contentMetricType: ContentMetricType,
    categoryType: CategoryType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    const [mainPeriodResponse, comparisonPeriodResponse] = await Promise.all([
      getContentScoreByCategoryInner(contentMetricType, categoryType, comparisonTimePeriod, voiceAndToneMatchTypes),
      getContentScoreByCategoryInner(contentMetricType, categoryType, mainTimePeriod, voiceAndToneMatchTypes)
    ]);

    return new CategoryScoreAdapter(categoryType, [mainPeriodResponse, comparisonPeriodResponse], {
      category: categoryNames,
      subcategory: subcategoryNames,
      brand: brandNames
    });
  };

  // TODO(tyan): fetch data from api given stacklineSku and time period
  const getProductLevelContentUtilizationDetails = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getContentUtilizationDetailMetricsForProduct(productRequestBuilder.build());
        return new ProductContentUtilizationAdapter(response.records);
      },
      new ProductContentUtilizationAdapter([]),
      'Error fetching product content utilization detail metrics'
    );
  };

  const getProductLevelContentAccuracyDetails = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getContentAccuracyDetailMetricsForProduct(productRequestBuilder.build());
        return new ProductContentAccuracyAdapter(response.records);
      },
      new ProductContentAccuracyAdapter([]),
      'Error fetching product content accuracy detail metrics'
    );
  };

  const getKeywordPerformanceScoreSummaryInner = async (timePeriod: TimePeriodRange) => {
    return withErrorHandling(
      async () => {
        const response = await client.getKeywordPerformanceScoreSummary(
          requestBuilder.clone().setMainTimePeriod(timePeriod).build()
        );
        return response.scores;
      },
      {} as KeywordPerformanceScores,
      `Error fetching keyword performance score summary for time period ${timePeriod} `
    );
  };

  const getKeywordPerformanceScoreSummary = async (
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainMetrics, comparisonMetrics] = await Promise.all([
      getKeywordPerformanceScoreSummaryInner(mainTimePeriod),
      getKeywordPerformanceScoreSummaryInner(comparisonTimePeriod)
    ]);

    return new KeywordsPerformanceSummaryAdapter(mainMetrics, comparisonMetrics);
  };

  const getProductLevelKeywordPerformanceDetails = async (
    keywordFilter: KeywordFilter,
    mainTimePeriod: TimePeriodRange
  ) => {
    return withErrorHandling(
      async () => {
        let detailsResponse: GetWeightedKeywordsAndPhrasesForProductResponse;
        switch (keywordFilter) {
          // TODO(tyan): update this once API endpoint is updated
          case KeywordFilter.Popularity:
            detailsResponse = await client.getWeightedKeywordsAndPhrasesForProduct(
              weightedKeywordsAndPhrasesRequestBuilder
                .clone()
                .setContentMetricType(ContentMetricType.KeywordSearchVolume)
                .build()
            );
            break;
          case KeywordFilter.Usage:
          case KeywordFilter.Productivity:
          default:
            detailsResponse = await client.getWeightedKeywordsAndPhrasesForProduct(
              weightedKeywordsAndPhrasesRequestBuilder
                .clone()
                .setContentMetricType(ContentMetricType.KeywordOrganicTraffic)
                .build()
            );
        }

        const summaryResponse = await client.getKeywordPerformanceScoreSummary(
          requestBuilder.setMainTimePeriod(mainTimePeriod).build()
        );

        return new ProductLevelKeywordsDetailsAdapter(detailsResponse, summaryResponse.scores);
      },
      new ProductLevelKeywordsDetailsAdapter(
        {} as GetWeightedKeywordsAndPhrasesForProductResponse,
        {} as KeywordPerformanceScores
      ),
      'Error fetching product level keyword performance details'
    );
  };

  const getContentScoresKeyMetricsByWeekInner = async (timePeriod: TimePeriodRange) => {
    return withErrorHandling(
      async () => {
        const response = await client.getContentScoreExtendedSummaryByWeek(
          requestBuilder.clone().setMainTimePeriod(timePeriod).build()
        );
        return response.records;
      },
      [],
      `Error fetching content scores key metrics by week for time period ${timePeriod} `
    );
  };

  const getContentScoresKeyMetricsByWeek = async (
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainMetrics, comparisonMetrics] = await Promise.all([
      getContentScoresKeyMetricsByWeekInner(mainTimePeriod),
      getContentScoresKeyMetricsByWeekInner(comparisonTimePeriod)
    ]);

    return new ContentScoresByWeekAdapter(mainMetrics, comparisonMetrics);
  };

  const getProductLevelVoiceAndToneAnalysis = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getVoiceAndToneCurrentAndTargetVectors(productRequestBuilder.build());
        return new ProductLevelVoiceAndToneAnalysisAdapter(response);
      },
      new ProductLevelVoiceAndToneAnalysisAdapter({} as GetVoiceAndToneCurrentAndTargetVectorsResponse),
      'Error fetching product level voice and tone analysis'
    );
  };

  const getProductContent = async (type: 'current' | 'approved' = 'current') => {
    return withErrorHandling(
      async () => {
        const response =
          type === 'current'
            ? await client.getCurrentContent(productRequestBuilder.build())
            : await client.getApprovedContent(productRequestBuilder.build());

        const currentVoiceAndTone = await client.getVoiceAndToneCurrentAndTargetVectors(productRequestBuilder.build());
        return new ProductLevelContentAdapter(response, currentVoiceAndTone);
      },
      new ProductLevelContentAdapter({} as GetContentResponse, {} as GetVoiceAndToneCurrentAndTargetVectorsResponse),
      `Error fetching ${type} content`
    );
  };

  const getProductCountByVoiceType = async (
    timePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    return withErrorHandling(
      async () => {
        const response = await client.getProductCountByVoiceType(
          requestBuilder.clone().setMainTimePeriod(timePeriod).setVoiceAndToneMatchTypes(voiceAndToneMatchTypes).build()
        );
        return response.records;
      },
      [],
      'Error fetching product count by voice type'
    );
  };

  const getProductCountByToneType = async (
    timePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    return withErrorHandling(
      async () => {
        const response = await client.getProductCountByToneType(
          requestBuilder.clone().setMainTimePeriod(timePeriod).setVoiceAndToneMatchTypes(voiceAndToneMatchTypes).build()
        );
        return response.records;
      },
      [],
      'Error fetching product count by tone type'
    );
  };

  const getProductCountByVoiceOrTone = async (
    type: 'voice' | 'tone',
    mainTimePeriod: TimePeriodRange,
    comparisonTimePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    const getCountByType = type === 'voice' ? getProductCountByVoiceType : getProductCountByToneType;
    const [mainResult, comparisonResult] = await Promise.all([
      getCountByType(mainTimePeriod, voiceAndToneMatchTypes),
      getCountByType(comparisonTimePeriod, voiceAndToneMatchTypes)
    ]);
    return new VoiceAndToneProductCountAdapter(type, [mainResult, comparisonResult]);
  };

  const compareProductKeywords = async (
    request: ProductKeywordsComparisonRequest
  ): Promise<ComparisonAnalysis> => {
    return withErrorHandling(
      async () => {
        const response = await apiCompareProductKeywords(request);
        return response.data.data;
      },
      {} as ComparisonAnalysis,
      'Error comparing product keywords'
    );
  };

  return {
    getContentScoresKeyMetrics,
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getProductLevelContentUtilizationDetails,
    getProductLevelContentAccuracyDetails,
    getKeywordPerformanceScoreSummary,
    getProductLevelKeywordPerformanceDetails,
    getContentScoresKeyMetricsByWeek,
    getProductLevelVoiceAndToneAnalysis,
    getProductContent,
    getProductCountByVoiceOrTone,
    getBeaconContentScoreByCategory,
    compareProductKeywords
  };
};
